import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import AdminGridBoardService from "../../pzl_grid/board/AdminGridBoardService";
import AdminCrossBoardService from "../../pzl_cross/board/AdminCrossBoardService";
import ConfirmDialog from "../../components/ConfirmDialog";
import Logger from "../../../services/Logger";
import TagsDialog from "../../../creator/common/TagsDialog";
import CustomTags from "../../components/CustomTags";

export default function Step3DetailsSection({
    puzzle,
    onError,
    onSuccess,
    onRefresh
}) {
    const [title, setTitle] = useState("");
    const [publishComment, setPublishComment] = useState("");
    const [isFree, setIsFree] = useState(false);
    const [tags, setTags] = useState([]);
    const [tagsDialogOpen, setTagsDialogOpen] = useState(false);

    // Apply title hook loading state
    const [applyingTitleHook, setApplyingTitleHook] = useState(false);
    const [showTitleHookDialog, setShowTitleHookDialog] = useState(false);

    // Initialize state from puzzle data
    useEffect(() => {
        if (puzzle) {
            setTitle(puzzle.title || "");
            setPublishComment(puzzle.comment || "");
            setIsFree(puzzle.free || false);
            setTags(Array.isArray(puzzle.tags) ? puzzle.tags : []);
        }
    }, [puzzle]);

    const handlePuzInfo = async () => {
        if (!puzzle?.id) {
            onError("Puzzle ID is required.");
            return;
        }

        // Validate title is not empty
        if (!title.trim()) {
            onError("Title is required and cannot be empty.");
            return;
        }

        // Validate title length
        if (title.length > 256) {
            onError("Title must be 256 characters or less.");
            return;
        }

        try {
            onError(null);
            // Determine which service to use based on puzzle type
            const isCrossword = puzzle.type === 'CROSSWORD';
            const updateService = isCrossword
                ? AdminCrossBoardService.updatePuzzleInfo
                : AdminGridBoardService.updatePuzzleInfo;

            // Format tags: ensure all are strings, trimmed, and non-empty
            const formattedTags = Array.isArray(tags) && tags.length > 0
                ? tags.map(tag => String(tag).trim()).filter(tag => tag.length > 0)
                : [];

            await updateService(puzzle.id, title.trim(), isFree, formattedTags);
            // Show success message using snackbar
            onSuccess("Puzzle information updated successfully", "success");
            // Refresh puzzle data
            if (onRefresh) {
                onRefresh();
            }
        } catch (err) {
            Logger.error("Error updating puzzle information:", err);
            onError("Failed to update puzzle information. Please try again.");
        }
    };

    const handleOpenTagsDialog = () => {
        setTagsDialogOpen(true);
    };

    const handleTagClick = (tag) => {
        // Add tag to the tags array if it's not already there (case-insensitive)
        const normalizedTag = tag.trim().toLowerCase();
        const isDuplicate = tags.some(t => t.trim().toLowerCase() === normalizedTag);

        if (!isDuplicate) {
            setTags([...tags, tag.trim()]);
        }
    };

    // Handle apply title hook button click - show confirmation
    const handleApplyTitleHook = () => {
        setShowTitleHookDialog(true);
    };

    const confirmApplyTitleHook = async () => {
        if (!puzzle?.id) return;

        try {
            setApplyingTitleHook(true);
            const isCrossword = puzzle.type === 'CROSSWORD';
            const service = isCrossword ? AdminCrossBoardService : AdminGridBoardService;
            const result = await service.applyTitleHook(puzzle.id);

            if (result.code && result.code === "FAILURE") {
                onError(result.message || "Failed to apply title hook");
            } else {
                onSuccess("Title hook applied successfully", "success");
                if (onRefresh) {
                    setTimeout(() => onRefresh(), 500);
                }
            }
        } catch (err) {
            Logger.error("Error applying title hook:", err);
            onError("Failed to apply title hook. Please try again.");
        } finally {
            setApplyingTitleHook(false);
        }
    };

    return (
        <Grid size={{ xs: 12 }}>
            <Paper elevation={2}>
                <Accordion
                    defaultExpanded={false}
                    disableGutters
                    elevation={0}
                    sx={{
                        "&:before": { display: "none" },
                        backgroundColor: "transparent",
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="puzzle-details-content"
                        id="puzzle-details-header"
                        sx={{ px: 3, py: 1.5 }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#3BA666" }}>
                            Puzzle Details
                        </Typography>
                        <Box
                            sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto", mr: 1 }}
                            onClick={(event) => event.stopPropagation()}
                            onFocus={(event) => event.stopPropagation()}
                        >
                            <Tooltip title="Change the title using the Bedrock" arrow>
                                <IconButton
                                    onClick={handleApplyTitleHook}
                                    disabled={applyingTitleHook || !puzzle?.id}
                                    sx={{
                                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                                        borderRadius: "50%",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                        flexShrink: 0,
                                        cursor: applyingTitleHook ? "wait" : "pointer",
                                        "&:hover": {
                                            backgroundColor: "primary.main",
                                            color: "white",
                                        },
                                        "&.Mui-disabled": {
                                            opacity: 0.7,
                                        },
                                    }}
                                >
                                    {applyingTitleHook ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <AutoAwesomeIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                        <Grid container spacing={0}>
                            <Grid size={12} sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter puzzle title..."
                                    variant="outlined"
                                    required
                                    slotProps={{ input: { maxLength: 256 } }}
                                    error={!title.trim() || title.length > 256}
                                    helperText={
                                        !title.trim()
                                            ? "Title is required"
                                            : title.length > 256
                                                ? `Title must be 256 characters or less (${title.length}/256)`
                                                : `${title.length}/256 characters`
                                    }
                                />
                            </Grid>
                            <Grid size={12}>
                                <CustomTags
                                    value={tags}
                                    onChange={(event, newTags) => setTags(newTags)}
                                    options={[]}
                                    onOpenTagsDialog={handleOpenTagsDialog}
                                    sx={{ mb: 2 }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Publishing Comment"
                                    value={publishComment}
                                    onChange={(e) => setPublishComment(e.target.value)}
                                    placeholder="Add any final notes before publishing..."
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid size={12}>
                                <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<SaveIcon />}
                                        onClick={handlePuzInfo}
                                        color="primary"
                                        disabled={!title.trim() || title.length > 256}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Paper>

            {/* Hook The Title Confirmation Dialog */}
            <ConfirmDialog
                open={showTitleHookDialog}
                setOpen={setShowTitleHookDialog}
                title="Hook The Title"
                message="Would you like to change the title using the Bedrock?"
                callback={confirmApplyTitleHook}
            />

            {/* Tags Dialog */}
            <TagsDialog
                open={tagsDialogOpen}
                onClose={() => setTagsDialogOpen(false)}
                onTagClick={handleTagClick}
            />
        </Grid>
    );
}
